import { useEffect, useRef } from 'react';
import { Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/format';
import type { RecomputeStatusDTO } from './types';

/**
 * `<RankingRecomputeButton>` — Phase 09 ADMIN-03 primary CTA. Top-right of
 * AdminPage. Reuses the Phase 07 SubmitForm slot #1 hover-ring pattern.
 *
 * State machine: idle / submitting / running / done / error. The parent
 * (AdminPage in Plan 09-03) supplies:
 *   - `status` from useRecomputeStatus()
 *   - `pending` from useTriggerRecompute().isPending
 *   - `onTrigger` calls useTriggerRecompute().mutate
 *
 * Status text region uses aria-live="polite" + aria-atomic="true" so screen
 * readers announce "Recomputing… N properties" updates AND the
 * "Recompute complete" final transition (T-09-07: pending+running disable
 * the button to prevent spam-click DoS).
 */
export interface RankingRecomputeButtonProps {
  status: RecomputeStatusDTO | undefined;
  pending: boolean;
  onTrigger: () => void;
}

const PRIMARY_BUTTON_CLASS = cn(
  'inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold',
  'bg-foam-50/10 text-foam-50 hover:bg-foam-50/15',
  'border border-white/10 hover:ring-1 hover:ring-bronze-500/40',
  'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
);

export function RankingRecomputeButton({
  status,
  pending,
  onTrigger,
}: RankingRecomputeButtonProps) {
  const wasRunningRef = useRef(false);
  const announceRef = useRef<HTMLSpanElement>(null);

  // Announce "Recompute complete · N" once when running flips false (and no error).
  useEffect(() => {
    if (
      wasRunningRef.current &&
      status &&
      !status.running &&
      !status.error
    ) {
      if (announceRef.current) {
        announceRef.current.textContent = `Recompute complete · ${status.processed} properties`;
      }
    }
    wasRunningRef.current = status?.running === true;
  }, [status]);

  // ERROR
  if (status?.error) {
    return (
      <div className="inline-flex items-center gap-3" role="alert">
        <span className="text-sm text-coral-500">
          Recompute failed: {status.error}
        </span>
        <button
          type="button"
          onClick={() => onTrigger()}
          disabled={pending}
          className={cn(
            PRIMARY_BUTTON_CLASS,
            pending && 'opacity-60 cursor-not-allowed',
          )}
        >
          {pending ? <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> : null}
          Retry
        </button>
      </div>
    );
  }

  // RUNNING (server says in-flight) — replaces the button with a status region.
  if (status?.running === true) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="inline-flex items-center gap-2 text-sm text-foam-50"
      >
        <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin text-tide-400" />
        <span>Recomputing… {status.processed} properties</span>
      </div>
    );
  }

  // DONE state shown alongside the re-enabled button when last_run is present.
  const showDone = !!(status && !status.running && status.last_run);

  return (
    <div className="inline-flex items-center gap-3">
      {showDone && (
        <span className="inline-flex items-center gap-2 text-sm text-moss-700">
          <Check aria-hidden="true" className="w-4 h-4" />
          Done · {status!.processed} properties ({formatRelativeTime(status!.last_run)})
        </span>
      )}
      <button
        type="button"
        onClick={() => onTrigger()}
        disabled={pending}
        className={cn(
          PRIMARY_BUTTON_CLASS,
          pending && 'opacity-60 cursor-not-allowed',
        )}
      >
        {pending ? (
          <>
            <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" />
            Submitting…
          </>
        ) : (
          'Recompute Rankings'
        )}
      </button>
      {/* Hidden live region used to announce one-shot transitions. */}
      <span ref={announceRef} className="sr-only" aria-live="polite" aria-atomic="true" />
    </div>
  );
}
